import { Type } from '@angular/core';
import { Route, Routes } from '@angular/router';

import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import type { PointCardComponent } from '../points/point-card/point-card.component';
import {
  featureFlagActiveGuard,
  featureFlagInactiveGuard,
} from '../../services/guards/feature-flag-guard';
import { COMMON_GUARD_SERVICES } from '../../services/guards/guard-services.const';
import { PointFullModalComponent } from '../points/point-full-modal/point-full-modal.component';
import { PointGalleryComponent } from '../points/point-gallery/point-gallery.component';
import { PointHalfModalComponent } from '../points/point-half-modal/point-half-modal.component';
import { PointRemindersComponent } from '../points/point-reminders/point-reminders.component';
import { SiteComponent } from './site-component/site.component';
import { SiteNewComponent } from './site-new/site-new.component';
import { SiteOverviewComponent } from './site-overview/site-overview.component';
import { ESiteSettingsPaths } from './site-routes.consts';
import { SiteSettingsCustomFieldsComponent } from './site-settings/site-settings-custom-fields/site-settings-custom-fields.component';
import { SiteSettingsIntegrationsComponent } from './site-settings/site-settings-integrations/site-settings-integrations.component';
import { SiteSettingsLogoComponent } from './site-settings/site-settings-logo/site-settings-logo.component';
import { SiteSettingsPlanComponent } from './site-settings/site-settings-plan/site-settings-plan.component';
import { SiteSettingsTagsComponent } from './site-settings/site-settings-tags/site-settings-tags.component';
import { SiteSettingsUsersComponent } from './site-settings/site-settings-users/site-settings-users.component';
import { SiteSettingsComponent } from './site-settings/site-settings.component';
import { SiteTimelineComponent } from './site-timeline/site-timeline.component';

const loadPointCardComponent = (): Promise<Type<PointCardComponent>> =>
  import('../points/point-card/point-card.component').then((m) => m.PointCardComponent);

const pointCardRemindersRoute: Route = {
  path: 'reminders',
  component: PointRemindersComponent,
  data: {
    type: 'half',
  },
};

export const buildPointModalRoutes = (): Routes => [
  {
    path: 'point/:_id',
    outlet: 'full',
    canMatch: [featureFlagActiveGuard(EFeatureFlag.CONVERT_POINT_CARD)],
    loadComponent: loadPointCardComponent,
    children: [pointCardRemindersRoute],
  },
  {
    path: 'point/:_id',
    canMatch: [featureFlagActiveGuard(EFeatureFlag.CONVERT_POINT_CARD)],
    loadComponent: loadPointCardComponent,
    children: [pointCardRemindersRoute],
  },
  {
    path: 'point/:_id',
    component: PointFullModalComponent,
    outlet: 'full',
    canMatch: [featureFlagInactiveGuard(EFeatureFlag.CONVERT_POINT_CARD)],
    children: [
      {
        path: 'gallery/:attachmentId',
        component: PointGalleryComponent,
      },
      {
        path: 'reminders',
        component: PointRemindersComponent,
        data: {
          type: 'full',
        },
      },
    ],
  },
  {
    path: 'point/:_id',
    component: PointHalfModalComponent,
    canMatch: [featureFlagInactiveGuard(EFeatureFlag.CONVERT_POINT_CARD)],
    children: [
      {
        path: 'gallery/:attachmentId',
        component: PointGalleryComponent,
      },
      {
        path: 'reminders',
        component: PointRemindersComponent,
        data: {
          type: 'half',
        },
      },
    ],
  },
];

export const siteRoutes: Routes = [
  {
    path: 'site/overview',
    component: SiteOverviewComponent,
    canActivate: COMMON_GUARD_SERVICES,
    // Points open even without that part but site is initialized again, which causes bugs
    children: buildPointModalRoutes(),
  },
  {
    path: 'site/timeline',
    component: SiteTimelineComponent,
    canActivate: COMMON_GUARD_SERVICES,
    children: buildPointModalRoutes(),
  },
  {
    path: 'site/:id',
    component: SiteComponent,
    canActivate: COMMON_GUARD_SERVICES,
    children: buildPointModalRoutes(),
  },

  {
    path: 'site',
    component: SiteComponent,
    canActivate: COMMON_GUARD_SERVICES,
  },

  {
    path: 'settings/site/new',
    component: SiteNewComponent,
    canActivate: COMMON_GUARD_SERVICES,
  },
  {
    path: 'settings/site/new/:id',
    component: SiteNewComponent,
    canActivate: COMMON_GUARD_SERVICES,
  },
  {
    path: 'settings/site/:id',
    component: SiteSettingsComponent,
    canActivate: COMMON_GUARD_SERVICES,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: ESiteSettingsPaths.TAGS,
      },
      {
        path: ESiteSettingsPaths.TAGS,
        component: SiteSettingsTagsComponent,
      },
      {
        path: ESiteSettingsPaths.IMPORT_POINTS,
        loadComponent: () =>
          import(
            './site-settings/site-settings-import-points/site-settings-import-points.component'
          ).then((m) => m.SiteSettingsImportPointsComponent),
      },
      {
        path: ESiteSettingsPaths.CUSTOM_FIELDS,
        canMatch: [featureFlagActiveGuard(EFeatureFlag.CUSTOM_FIELD_GROUPS)],
        loadComponent: () =>
          import(
            './site-settings/site-settings-custom-field-groups/site-settings-custom-field-groups.component'
          ).then((m) => m.SiteSettingsCustomFieldGroupsComponent),
      },
      {
        path: ESiteSettingsPaths.CUSTOM_FIELDS,
        canMatch: [featureFlagInactiveGuard(EFeatureFlag.CUSTOM_FIELD_GROUPS)],
        component: SiteSettingsCustomFieldsComponent,
      },
      {
        path: ESiteSettingsPaths.USERS,
        component: SiteSettingsUsersComponent,
      },
      {
        path: ESiteSettingsPaths.PLAN,
        component: SiteSettingsPlanComponent,
      },
      {
        path: ESiteSettingsPaths.LOGO,
        component: SiteSettingsLogoComponent,
      },
      {
        path: ESiteSettingsPaths.INTEGRATIONS,
        component: SiteSettingsIntegrationsComponent,
      },
    ],
  },
];
